import { IFontStyle, IFontStyles, ThemeType } from 'theme'

type WithOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

const helperFontStyle = ({
  color,
  fontFamily,
  fontSize,
  lineHeight,
  letterSpacing,
}: WithOptional<
  IFontStyle,
  'color' | 'fontFamily' | 'lineHeight' | 'letterSpacing'
>) => ({
  fontFamily: fontFamily ?? 'GT-Eesti-Text-Regular',
  fontSize,
  lineHeight: lineHeight ?? 1,
  color: color ?? 'white',
  letterSpacing: letterSpacing ?? 'normal',
})

export const FONT_SIZES = (projectTheme: ThemeType): IFontStyles => {
  return {
    h1: {
      mainContent: {
        desktop: helperFontStyle({
          fontFamily: projectTheme.fonts.bold.serif,
          fontSize: '8.125rem',
          lineHeight: 0.95,
        }),
        tablet: helperFontStyle({
          fontFamily: projectTheme.fonts.bold.serif,
          fontSize: '5rem',
          lineHeight: 0.9,
        }),
        phone: helperFontStyle({
          fontFamily: projectTheme.fonts.bold.serif,
          fontSize: '2.5rem',
        }),
      },
      subContent: {
        desktop: helperFontStyle({ fontSize: '0rem', lineHeight: 0 }),
        tablet: helperFontStyle({ fontSize: '0rem', lineHeight: 0 }),
        phone: helperFontStyle({ fontSize: '0rem', lineHeight: 0 }),
      },
    },
    h2: {
      mainContent: {
        desktop: helperFontStyle({
          fontFamily: projectTheme.fonts.bold.serif,
          fontSize: '4.0625rem',
        }),
        tablet: helperFontStyle({
          fontFamily: projectTheme.fonts.bold.serif,
          fontSize: '3.125rem',
          lineHeight: 1.1,
        }),
        phone: helperFontStyle({
          fontFamily: projectTheme.fonts.bold.serif,
          fontSize: '1.875rem',
          lineHeight: 1,
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontFamily: projectTheme.fonts.bold.serif,
          fontSize: '4.0625rem',
          lineHeight: 0.92,
          color: projectTheme.palette.black(),
        }),
        tablet: helperFontStyle({
          fontFamily: projectTheme.fonts.bold.serif,
          fontSize: '3.125rem',
          lineHeight: 1.1,
          color: projectTheme.palette.black(),
        }),
        phone: helperFontStyle({
          fontFamily: projectTheme.fonts.bold.serif,
          fontSize: '1.875rem',
          lineHeight: 1.1,
          color: projectTheme.palette.black(),
        }),
      },
    },
    h3: {
      mainContent: {
        desktop: helperFontStyle({
          fontSize: '2.8125rem',
        }),
        tablet: helperFontStyle({
          fontSize: '1.875rem',
          lineHeight: 1.1,
        }),
        phone: helperFontStyle({
          fontSize: '1.5625rem',
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontSize: '2.8125rem',
          color: projectTheme.palette.black(),
        }),
        tablet: helperFontStyle({
          fontSize: '1.875rem',
          lineHeight: 1.1,
          color: projectTheme.palette.black(),
        }),
        phone: helperFontStyle({
          fontSize: '1.875rem',
          lineHeight: 1.1,
          color: projectTheme.palette.black(),
        }),
      },
    },
    h4: {
      mainContent: {
        desktop: helperFontStyle({
          fontSize: '2.1875rem',
        }),
        tablet: helperFontStyle({
          fontSize: '1.5625rem',
          lineHeight: 1.2,
        }),
        phone: helperFontStyle({
          fontSize: '1.5625rem',
          lineHeight: 1.2,
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontSize: '2.1875rem',
          color: projectTheme.palette.black(),
        }),
        tablet: helperFontStyle({
          fontSize: '1.5625rem',
          lineHeight: 1.2,
          color: projectTheme.palette.black(),
        }),
        phone: helperFontStyle({
          fontSize: '1.5625rem',
          lineHeight: 1.2,
          color: projectTheme.palette.black(),
        }),
      },
    },
    h5: {
      mainContent: {
        desktop: helperFontStyle({
          fontSize: '2.1875rem',
          color: projectTheme.palette.primary.main,
        }),
        tablet: helperFontStyle({
          fontSize: '1.5625rem',
          color: projectTheme.palette.primary.main,
          lineHeight: 1.2,
        }),
        phone: helperFontStyle({
          fontSize: '1.5625rem',
          color: projectTheme.palette.primary.main,
          lineHeight: 1.2,
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontSize: '2.1875rem',
          color: projectTheme.palette.black(),
          fontFamily: projectTheme.fonts.bold.serif,
        }),
        tablet: helperFontStyle({
          fontSize: '1.5625rem',
          lineHeight: 1.2,
          color: projectTheme.palette.black(),
          fontFamily: projectTheme.fonts.bold.serif,
        }),
        phone: helperFontStyle({
          fontSize: '1.375rem',
          lineHeight: 1.1,
          color: projectTheme.palette.black(),
          fontFamily: projectTheme.fonts.bold.serif,
        }),
      },
    },
    body1: {
      mainContent: {
        desktop: helperFontStyle({
          fontSize: '1.25rem',
          lineHeight: 1.5,
        }),
        tablet: helperFontStyle({
          fontSize: '1.125rem',
          lineHeight: 1.44,
        }),
        phone: helperFontStyle({
          fontSize: '1rem',
          lineHeight: 1.5,
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontSize: '1.25rem',
          color: projectTheme.palette.black(),
          lineHeight: 1.5,
        }),
        tablet: helperFontStyle({
          fontSize: '1.125rem',
          lineHeight: 1.44,
          color: projectTheme.palette.black(),
        }),
        phone: helperFontStyle({
          fontSize: '1.125rem',
          lineHeight: 1.44,
          color: projectTheme.palette.black(),
        }),
      },
    },
    body2: {
      mainContent: {
        desktop: helperFontStyle({
          fontSize: '1.375rem',
          lineHeight: 1.36,
        }),
        tablet: helperFontStyle({
          fontSize: '1rem',
          lineHeight: 1.375,
        }),
        phone: helperFontStyle({
          fontSize: '0.9375rem',
          lineHeight: 1.33,
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontSize: '1.375rem',
          color: projectTheme.palette.black(),
          lineHeight: 1.36,
        }),
        tablet: helperFontStyle({
          fontSize: '1rem',
          lineHeight: 1.375,
          color: projectTheme.palette.black(),
        }),
        phone: helperFontStyle({
          fontSize: '0.875rem',
          lineHeight: 1.43,
          color: projectTheme.palette.black(),
        }),
      },
    },
    category: {
      mainContent: {
        desktop: helperFontStyle({
          fontSize: '2.5rem',
          lineHeight: 1.125,
        }),
        tablet: helperFontStyle({
          fontSize: '1.375rem',
          lineHeight: 1.18,
        }),
        phone: helperFontStyle({
          fontSize: '1.125rem',
          lineHeight: 1.22,
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontSize: '0',
        }),
        tablet: helperFontStyle({
          fontSize: '0',
        }),
        phone: helperFontStyle({
          fontSize: '0',
        }),
      },
    },
    quotes: {
      mainContent: {
        desktop: helperFontStyle({
          fontSize: '1.875rem',
          lineHeight: 1.16,
          letterSpacing: '0.3px',
        }),
        tablet: helperFontStyle({
          fontSize: '1.5625rem',
          lineHeight: 1.2,
          letterSpacing: '0.25px',
        }),
        phone: helperFontStyle({
          fontSize: '1.375rem',
          lineHeight: 1.1,
          letterSpacing: '0.22px',
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontSize: '1.875rem',
          lineHeight: 1.16,
          letterSpacing: '0.3px',
        }),
        tablet: helperFontStyle({
          fontSize: '1.5625rem',
          lineHeight: 1.2,
          letterSpacing: '0.25px',
        }),
        phone: helperFontStyle({
          fontSize: '1.375rem',
          lineHeight: 1.1,
          letterSpacing: '0.22px',
        }),
      },
    },
    button: {
      mainContent: {
        desktop: helperFontStyle({
          fontSize: '1.125rem',
          lineHeight: 1.22,
        }),
        tablet: helperFontStyle({
          fontSize: '1.125rem',
          lineHeight: 1.2,
        }),
        phone: helperFontStyle({
          fontSize: '0.875rem',
          lineHeight: 1.29,
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontSize: '1.125rem',
          lineHeight: 1.22,
        }),
        tablet: helperFontStyle({
          fontSize: '1rem',
          lineHeight: 1.375,
        }),
        phone: helperFontStyle({
          fontSize: '1rem',
          lineHeight: 1.375,
        }),
      },
    },
    caption: {
      mainContent: {
        desktop: helperFontStyle({
          fontSize: '1.125rem',
          lineHeight: 1.39,
        }),
        tablet: helperFontStyle({
          fontSize: '1.0625rem',
          lineHeight: 1.35,
        }),
        phone: helperFontStyle({
          fontSize: '0.875rem',
          lineHeight: 1.29,
        }),
      },
      subContent: {
        desktop: helperFontStyle({
          fontSize: '0',
        }),
        tablet: helperFontStyle({
          fontSize: '0',
        }),
        phone: helperFontStyle({
          fontSize: '0',
        }),
      },
    },
  }
}
